import * as THREE from 'three';
import { TICK_DT, getHeroDef, heroStats, lerpAngle, type AbilitySlot, type HeroState } from '@moba/shared';
import { clone as cloneSkeleton } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { instantiateCharacter, makeGlowTexture, propModels, type CharacterAsset, type CharacterInstance, type ClipTiming } from './assets.ts';
import { ISO_YAW } from './CameraRig.ts';
import { ThornsBubble } from './ThornsBubble.ts';
import { StunSwirl } from './StunSwirl.ts';
import { RecallFx } from './RecallFx.ts';

import { heroDisplayName } from './i18n.ts';
/** `serial` de un lanzamiento predicho que el servidor aún no ha confirmado. */
const PREDICTED_SERIAL = -2;

/** Giro (rotation.y) con el que el héroe mira hacia la cámara isométrica. */
const FACE_CAMERA_YAW = ISO_YAW - Math.PI;

/** Altura máxima (u) del salto del agarre aéreo. */
const LIFT_HEIGHT = 3.4;
/** La presa del agarre va por encima de la cabeza de Bowser (si no, su cuerpo la tapa en la vista isométrica). */
const HELD_EXTRA_HEIGHT = 3.2;

/** estela de la bola de fuego: una llama cada FLAME_INTERVAL s que dura FLAME_LIFE s */
const FLAME_INTERVAL = 0.025;
const FLAME_LIFE = 0.38;
let flameTexture: THREE.CanvasTexture | null = null;

interface Flame {
  sprite: THREE.Sprite;
  life: number;
  size: number;
}

interface Shield {
  clips: Set<string>;
  group: THREE.Group;
  faces: THREE.SpriteMaterial;
  rim: THREE.SpriteMaterial;
  rays: THREE.Sprite;
  stars: Array<{ sprite: THREE.Sprite; phase: number; size: number }>;
  height: number;
  radius: number;
}

let starTexture: THREE.CanvasTexture | null = null;
let raysTexture: THREE.CanvasTexture | null = null;
let hexTexture: THREE.CanvasTexture | null = null;
let hexRimTexture: THREE.CanvasTexture | null = null;

/** Contorno de un hexágono con punta arriba (lados verticales a izquierda y derecha). */
function hexPath(ctx: CanvasRenderingContext2D, c: number, r: number): void {
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const a = -Math.PI / 2 + (i * Math.PI) / 3;
    const x = c + Math.cos(a) * r;
    const y = c + Math.sin(a) * r;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

/** Panel hexagonal blanco translúcido, algo más opaco y azulado hacia el borde. */
function makeHexTexture(size = 256): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const c = size / 2;
  hexPath(ctx, c, c * 0.96);
  const fill = ctx.createRadialGradient(c, c, 0, c, c, c);
  fill.addColorStop(0, 'rgba(255,255,255,0.55)');
  fill.addColorStop(0.7, 'rgba(240,250,255,0.72)');
  fill.addColorStop(1, 'rgba(200,232,255,0.92)');
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.lineWidth = size * 0.02;
  ctx.strokeStyle = 'rgba(170,220,255,0.95)';
  ctx.stroke();
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

/** Halo azulado del contorno (se suma por detrás del panel). */
function makeHexRimTexture(size = 256): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const c = size / 2;
  ctx.shadowColor = 'rgba(90,180,255,1)';
  ctx.shadowBlur = size * 0.08;
  ctx.lineWidth = size * 0.05;
  ctx.strokeStyle = 'rgba(120,200,255,0.9)';
  hexPath(ctx, c, c * 0.84);
  ctx.stroke();
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

/** Destello de cuatro puntas (los "brillos" azules del Reflector de Melee). */
function makeStarTexture(size = 64): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const c = size / 2;
  const glow = ctx.createRadialGradient(c, c, 0, c, c, c);
  glow.addColorStop(0, 'rgba(255,255,255,0.9)');
  glow.addColorStop(0.25, 'rgba(255,255,255,0.25)');
  glow.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, size, size);
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  const arm = c * 0.95;
  const waist = c * 0.13;
  ctx.moveTo(c, c - arm);
  ctx.quadraticCurveTo(c + waist, c - waist, c + arm, c);
  ctx.quadraticCurveTo(c + waist, c + waist, c, c + arm);
  ctx.quadraticCurveTo(c - waist, c + waist, c - arm, c);
  ctx.quadraticCurveTo(c - waist, c - waist, c, c - arm);
  ctx.fill();
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

/** Estallido de rayos de luz que irradian desde el centro. */
function makeRaysTexture(size = 256): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const c = size / 2;
  ctx.translate(c, c);
  for (let i = 0; i < 28; i++) {
    const angle = (i / 28) * Math.PI * 2 + (i % 3) * 0.05;
    const length = c * (0.55 + ((i * 37) % 10) / 22);
    const width = 1.5 + ((i * 13) % 4);
    const grad = ctx.createLinearGradient(0, 0, Math.cos(angle) * length, Math.sin(angle) * length);
    grad.addColorStop(0, 'rgba(255,255,255,0.9)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.strokeStyle = grad;
    ctx.lineWidth = width;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(Math.cos(angle) * length, Math.sin(angle) * length);
    ctx.stroke();
  }
  const core = ctx.createRadialGradient(0, 0, 0, 0, 0, c * 0.5);
  core.addColorStop(0, 'rgba(255,255,255,0.8)');
  core.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = core;
  ctx.fillRect(-c, -c, size, size);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

interface Fireball {
  clips: Set<string>;
  group: THREE.Group;
  core: THREE.Mesh;
  shell: THREE.Mesh;
  glow: THREE.Sprite;
  radius: number;
  height: number;
}

/** cada cuánto (s) se deja una copia fantasma y cuánto dura */
const AFTERIMAGE_INTERVAL = 0.03;
const AFTERIMAGE_LIFE = 0.26;

interface Afterimage {
  object: THREE.Object3D;
  materials: THREE.MeshBasicMaterial[];
  life: number;
}

const TEAM_HEX: Record<HeroState['team'], number> = {
  blue: 0x4da3ff,
  red: 0xff5a5a,
};

const TARGET_HEX = 0xffffff;

const TEAM_CSS: Record<HeroState['team'], string> = {
  blue: '#4da3ff',
  red: '#ff5a5a',
};

interface DesiredClip {
  key: string;
  timeScale: number;
  serial: number;
  /** duración de la acción (ticks): el clip se ajusta a ella (ver `ClipWarp`) */
  durationTicks?: number;
  /** tick de la acción en que la simulación golpea / dispara */
  impactTicks?: number;
}

/**
 * Reproducción de un golpe ajustada a la simulación: el tramo del clip
 * `start -> impact` dura lo que la acción hasta su tick de impacto y
 * `impact -> end` el resto, así el golpe visible cae cuando la simulación hace
 * daño. Sin impacto, `start -> end` se reparte de forma lineal.
 */
interface ClipWarp {
  start: number;
  end: number;
  impact?: number;
  impactT: number;
  durationT: number;
  elapsed: number;
}

/** Fundido entre clips según la transición (s). */
function fadeTime(from: string, to: string): number {
  const strike = (key: string) => key.startsWith('attack') || key.startsWith('cast_');
  if (to === 'death') return 0.12;
  if (to === 'hit') return 0.05;
  if (strike(to)) return 0.06;
  if (strike(from) || from === 'hit') return 0.16;
  if ((from === 'idle' && to === 'run') || (from === 'run' && to === 'idle')) return 0.18;
  return 0.12;
}

export class HeroView {
  readonly root = new THREE.Group();
  readonly model: THREE.Object3D;

  private readonly instance: CharacterInstance;
  private readonly mixer: THREE.AnimationMixer;
  private readonly actions = new Map<string, THREE.AnimationAction>();
  private readonly clipDurations = new Map<string, number>();
  private readonly materials: THREE.MeshStandardMaterial[] = [];
  private readonly ringMaterial: THREE.MeshBasicMaterial;
  private readonly markerMaterial: THREE.MeshBasicMaterial;
  private readonly hpCanvas: HTMLCanvasElement;
  private readonly hpTexture: THREE.CanvasTexture;
  private readonly hpSprite: THREE.Sprite;
  private readonly barY: number;

  private readonly loopKeys: Set<string>;
  private readonly bow?: { group: THREE.Group; clip: string; bones: THREE.Object3D[] };
  private readonly blaster?: { group: THREE.Group; clips: Set<string>; bones: THREE.Object3D[] };
  private readonly scratch = new THREE.Vector3();
  private readonly bowPose?: { bowArm: THREE.Object3D[]; drawArm: THREE.Object3D[] };
  private readonly reflectorPose?: {
    clips: Set<string>;
    spine: THREE.Object3D[];
    legs: THREE.Object3D[][];
    arms: THREE.Object3D[][];
    squat: number;
    baked: boolean;
    height: number;
    baseY: number;
  };
  private readonly afterimageSpecs: Array<{ clips: Set<string>; color: THREE.Color }>;
  private readonly burn?: { clips: Set<string>; color: THREE.Color };
  private readonly fireball?: Fireball;
  private readonly shield?: Shield;
  /** burbuja de espinas, sólo en héroes con una habilidad `shield` (W de Bowser) */
  private readonly thorns?: ThornsBubble;
  /** remolino violeta mientras el héroe está aturdido */
  private readonly stunSwirl: StunSwirl;
  private readonly flames: Flame[] = [];
  private readonly jumpSpec?: { clips: Set<string>; height: number; coins: boolean; bodyHeight: number };
  private jumpSerial = -1;
  private jumpLandPending = false;
  private readonly jumpBits: JumpBit[] = [];
  private readonly slamSpec?: { clips: Set<string>; height: number };
  private readonly breathPose?: {
    clips: Set<string>;
    chest: THREE.Object3D;
    neck: THREE.Object3D[];
    head: THREE.Object3D;
    jaw: THREE.Object3D;
  };
  private readonly spinSpecs: Array<{ clips: Set<string>; turns: number; crouch: number; ticks: number }> = [];
  private spinActive = false;
  /** serial de la acción de agarre que ya despegó (se queda con el clip del estrellón) */
  private grabSlamSerial = -1;
  private readonly grabSlamKey?: string;
  private slamSerial = -1;
  private slamLanded = false;
  /** aplastamiento al aterrizar del golpe (1 -> 0) */
  private squash = 0;
  private readonly modelScale = new THREE.Vector3(1, 1, 1);
  /** el golpe contra el suelo llegó al suelo: temblor de cámara (lo conecta Game) */
  onSlam?: (x: number, z: number) => void;
  private readonly breath?: {
    clips: Set<string>;
    bone: THREE.Object3D;
    forward: number;
    down: number;
    range: number;
    arc: number;
    startTick: number;
    height: number;
  };
  private breathTimer = 0;
  private readonly breathFlames: BreathFlame[] = [];
  /** anillo de equipo/selección: se queda en el suelo aunque el héroe salte */
  private groundRing?: THREE.Object3D;
  private flameTimer = 0;
  private readonly afterimages: Afterimage[] = [];
  private afterimageTimer = 0;
  /** sello mágico y motas de luz mientras canaliza el regreso a base */
  private readonly recallFx: RecallFx;
  private currentAction: THREE.AnimationAction | null = null;
  private currentKey = '';
  /** clip de la acción en curso (aunque se reproduzca otro, p. ej. correr con el Reflector) */
  private actionKey = '';
  private currentSerial = -1;
  private warp: ClipWarp | null = null;
  private readonly timings: Record<string, ClipTiming>;
  /** velocidad del pie apoyado en el clip de carrera (u/s): la carrera va a la velocidad del héroe */
  private readonly runSpeed: number;
  private runStatsKey = '';
  private runMoveSpeed = 0;
  /** lanzamiento propio empezado antes de que lo confirme el servidor (online) */
  private predictedCast: {
    slot: AbilitySlot;
    duration: number;
    baseSerial: number;
    until: number;
    yaw: number;
  } | null = null;
  private lastHp = -1;
  private lastShield = 0;
  /** agarre aéreo (R de Bowser): está en el aire; al aterrizar suelta polvo */
  private lifted = false;
  private predictOffsetX = 0;
  private predictOffsetZ = 0;
  private predictMoving = false;
  private predictYaw = 0;
  /**
   * Pies en el suelo durante el idle: los idle retargeteados de KayKit suben la
   * cadera sin doblar las piernas y el héroe flotaba (Link, Bowser, Samus,
   * Mario). Se baja el modelo lo que el pie más bajo se separa de su altura en
   * reposo. `footBones` son los huesos más bajos en la pose de enlace.
   */
  private readonly footBones: THREE.Object3D[] = [];
  private footBaseline = 0;
  private groundOffset = 0;
  private readonly footTmp = new THREE.Vector3();
  private readonly modelBase = new THREE.Vector3();
  private readonly bodyHeight: number;
  private lastAlive = true;
  private lastLevel = 0;
  private targeted = false;

  constructor(
    readonly hero: HeroState,
    asset: CharacterAsset,
  ) {
    this.instance = instantiateCharacter(asset);
    this.model = this.instance.model;
    this.root.add(this.model);
    this.modelBase.copy(this.model.position);
    this.bodyHeight = asset.manifest.targetHeight;
    this.root.position.set(hero.pos.x, 0, hero.pos.z);
    this.root.rotation.y = hero.facing;

    this.loopKeys = new Set(['idle', 'run', ...(asset.manifest.loop ?? [])]);
    this.runSpeed = asset.manifest.runSpeed ?? 0;
    this.timings = asset.manifest.timing ?? {};
    this.mixer = new THREE.AnimationMixer(this.model);
    for (const [key, clipName] of Object.entries(asset.manifest.clips)) {
      const clip = THREE.AnimationClip.findByName(this.instance.clips, clipName);
      if (!clip) continue;
      this.actions.set(key, this.mixer.clipAction(clip));
      this.clipDurations.set(key, clip.duration);
    }
    this.findFootBones(asset.manifest.feet);

    this.model.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh) return;
      const source = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      const cloned = source.map((material) => {
        const copy = material.clone();
        if (copy instanceof THREE.MeshStandardMaterial) {
          copy.emissive = new THREE.Color(0x000000);
          copy.emissiveIntensity = 0;
          this.materials.push(copy);
        }
        return copy;
      });
      mesh.material = Array.isArray(mesh.material) ? cloned : cloned[0];
    });

    const ringGeometry = new THREE.RingGeometry(hero.radius * 1.02, hero.radius * 1.5, 40);
    ringGeometry.rotateX(-Math.PI / 2);
    this.ringMaterial = new THREE.MeshBasicMaterial({
      color: TEAM_HEX[hero.team],
      transparent: true,
      opacity: hero.isPlayer ? 0.8 : 0.45,
      depthWrite: false,
      side: THREE.DoubleSide, forceSinglePass: true,
    });
    const ring = new THREE.Mesh(ringGeometry, this.ringMaterial);
    ring.position.y = 0.04;
    this.root.add(ring);
    this.groundRing = ring;

    const markerGeometry = new THREE.ConeGeometry(0.14, 0.4, 3);
    markerGeometry.rotateX(Math.PI / 2);
    this.markerMaterial = new THREE.MeshBasicMaterial({
      color: TEAM_HEX[hero.team],
      transparent: true,
      opacity: 0.8,
      depthWrite: false,
    });
    const marker = new THREE.Mesh(markerGeometry, this.markerMaterial);
    marker.position.set(0, 0.06, hero.radius + 0.45);
    this.root.add(marker);

    this.hpCanvas = document.createElement('canvas');
    this.hpCanvas.width = 256;
    this.hpCanvas.height = 56;
    this.hpTexture = new THREE.CanvasTexture(this.hpCanvas);
    this.hpTexture.colorSpace = THREE.SRGBColorSpace;
    this.hpTexture.minFilter = THREE.LinearFilter;
    this.barY = asset.manifest.targetHeight + 0.62;

    this.hpSprite = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: this.hpTexture,
        transparent: true,
        depthTest: false,
        depthWrite: false,
      }),
    );
    this.hpSprite.scale.set(2.7, 0.59, 1);
    this.hpSprite.position.set(0, this.barY, 0);
    this.hpSprite.renderOrder = 30;
    this.root.add(this.hpSprite);

    this.drawHp();

    this.recallFx = new RecallFx(hero.radius, asset.manifest.targetHeight, TEAM_HEX[hero.team]);
    this.root.add(this.recallFx.group);

    this.afterimageSpecs = (asset.manifest.afterimages ?? []).map((spec) => ({
      clips: new Set(spec.clips),
      color: new THREE.Color(spec.color),
    }));
    if (asset.manifest.burn) {
      this.burn = { clips: new Set(asset.manifest.burn.clips), color: new THREE.Color(asset.manifest.burn.color) };
    }

    if (asset.manifest.shield) {
      this.shield = makeShield(
        asset.manifest.targetHeight,
        new Set(asset.manifest.shield.clips),
        new THREE.Color(asset.manifest.shield.color),
      );
      this.root.add(this.shield.group);
    }

    if (Object.values(getHeroDef(hero.heroId).abilities).some((ability) => ability.kind === 'shield')) {
      this.thorns = new ThornsBubble(hero.radius, asset.manifest.targetHeight);
      this.root.add(this.thorns.group);
    }

    this.stunSwirl = new StunSwirl(hero.radius, asset.manifest.targetHeight);
    this.root.add(this.stunSwirl.group);

    const breathSpec = asset.manifest.breath;
    const breathBone = breathSpec ? this.model.getObjectByName(breathSpec.bone) : undefined;
    if (breathSpec && breathBone) {
      this.breath = {
        clips: new Set(breathSpec.clips),
        bone: breathBone,
        forward: breathSpec.forward,
        down: breathSpec.down,
        range: breathSpec.range,
        arc: breathSpec.arc,
        startTick: breathSpec.startTick,
        height: asset.manifest.targetHeight,
      };
    }

    if (asset.manifest.slam) {
      this.slamSpec = { clips: new Set(asset.manifest.slam.clips), height: asset.manifest.slam.height };
    }
    const breath = asset.manifest.breathPose;
    if (breath) {
      const bone = (name: string) => this.model.getObjectByName(name);
      const chest = bone(breath.chest);
      const head = bone(breath.head);
      const jaw = bone(breath.jaw);
      const neck = breath.neck.map(bone).filter((b): b is THREE.Object3D => Boolean(b));
      if (chest && head && jaw) this.breathPose = { clips: new Set(breath.clips), chest, neck, head, jaw };
    }
    for (const spin of asset.manifest.spin ?? []) {
      this.spinSpecs.push({ clips: new Set(spin.clips), turns: spin.turns, crouch: spin.crouch ?? 0, ticks: spin.ticks ?? 0 });
    }
    this.grabSlamKey = asset.manifest.grabSlam;
    this.modelScale.copy(this.model.scale);

    if (asset.manifest.jump) {
      const spec = asset.manifest.jump;
      this.jumpSpec = { clips: new Set(spec.clips), height: spec.height, coins: Boolean(spec.coins), bodyHeight: asset.manifest.targetHeight };
    }

    if (asset.manifest.fireball) {
      this.fireball = makeFireball(asset.manifest.targetHeight, new Set(asset.manifest.fireball.clips));
      this.root.add(this.fireball.group);
    }

    const reflectorSpec = asset.manifest.reflectorPose;
    if (reflectorSpec) {
      const find = (names: string[]) => names.map((name) => this.model.getObjectByName(name));
      const spine = find(reflectorSpec.spine);
      const legs = reflectorSpec.legs.map(find);
      const arms = reflectorSpec.arms.map(find);
      if ([...spine, ...legs.flat(), ...arms.flat()].every(Boolean)) {
        this.reflectorPose = {
          clips: new Set(reflectorSpec.clips),
          spine: spine as THREE.Object3D[],
          legs: legs as THREE.Object3D[][],
          arms: arms as THREE.Object3D[][],
          squat: reflectorSpec.squat,
          baked: Boolean(reflectorSpec.baked),
          height: asset.manifest.targetHeight,
          baseY: this.model.position.y,
        };
      }
    }

    const poseSpec = asset.manifest.bowPose;
    if (poseSpec) {
      const find = (names: string[]) => names.map((name) => this.model.getObjectByName(name));
      const bowArm = find(poseSpec.bowArm);
      const drawArm = find(poseSpec.drawArm);
      if ([...bowArm, ...drawArm].every(Boolean)) {
        this.bowPose = { bowArm: bowArm as THREE.Object3D[], drawArm: drawArm as THREE.Object3D[] };
      }
    }

    const bowSpec = asset.manifest.bow;
    if (bowSpec) {
      const bones = bowSpec.bones
        .map((name) => this.model.getObjectByName(name))
        .filter((bone): bone is THREE.Object3D => Boolean(bone));
      if (bones.length) {
        const group = makeBow(asset.manifest.targetHeight);
        group.visible = false;
        this.root.add(group);
        this.bow = { group, clip: bowSpec.clip, bones };
      }
    }

    const blasterSpec = asset.manifest.blaster;
    if (blasterSpec) {
      const bones = blasterSpec.bones
        .map((name) => this.model.getObjectByName(name))
        .filter((bone): bone is THREE.Object3D => Boolean(bone));
      if (bones.length) {
        const group = makeBlaster(asset.manifest.targetHeight);
        group.visible = false;
        this.root.add(group);
        this.blaster = { group, clips: new Set(blasterSpec.clips), bones };
      }
    }
  }

  /** Marca este héroe como objetivo fijado del jugador local. */
  setTargeted(targeted: boolean): void {
    this.targeted = targeted;
  }

  /**
   * Predicción local (online, sólo el héroe propio): desplazamiento respecto a
   * la posición del servidor y, si camina, hacia dónde. Así responde al clic al
   * instante en vez de esperar la ida y vuelta.
   */
  /** Los golpes contra el suelo no se predicen: su salto sigue los ticks del servidor. */
  canPredictCast(slot: AbilitySlot): boolean {
    return !this.slamSpec?.clips.has(`cast_${slot}`);
  }

  get hasPredictedCast(): boolean {
    return this.predictedCast !== null;
  }

  /**
   * Empieza ya la animación de la habilidad `slot` (el servidor la confirmará
   * en ~`windowMs`). Si llega su acción, se adopta sin reiniciar el clip; si
   * llega otra o vence el plazo, se descarta.
   */
  predictCast(slot: AbilitySlot, durationTicks: number, windowMs: number, yaw: number): void {
    this.predictedCast = {
      slot,
      duration: durationTicks,
      baseSerial: this.hero.actionSerial,
      until: performance.now() + windowMs,
      yaw,
    };
  }

  setPrediction(offsetX: number, offsetZ: number, moving: boolean, yaw: number): void {
    this.predictOffsetX = offsetX;
    this.predictOffsetZ = offsetZ;
    this.predictMoving = moving;
    this.predictYaw = yaw;
  }

  /** tiempo de animación pendiente mientras no se anima (fuera de cámara) */
  private animDebt = 0;

  /** `animate`: false si no se ve (fuera de cámara o en la niebla): se salta el esqueleto. */
  update(dt: number, alpha: number, animate = true): void {
    const hero = this.hero;
    const x = hero.prevPos.x + (hero.pos.x - hero.prevPos.x) * alpha + this.predictOffsetX;
    const z = hero.prevPos.z + (hero.pos.z - hero.prevPos.z) * alpha + this.predictOffsetZ;
    this.root.position.set(x, 0, z);

    const moving =
      this.predictMoving ||
      (hero.pos.x - hero.prevPos.x) ** 2 + (hero.pos.z - hero.prevPos.z) ** 2 > 0.000002;
    let desired = this.desiredClip(moving);
    this.actionKey = desired.key;
    // el Reflector no paraliza: quieto se planta de frente a la cámara como en
    // Melee; si se mueve, corre hacia donde va con el escudo puesto
    const reflecting = hero.alive && Boolean(this.reflectorPose?.clips.has(desired.key));
    if (reflecting && moving) desired = { key: 'run', timeScale: 1, serial: desired.serial };
    const faceCamera = reflecting && !moving;
    const castYaw = desired.serial === PREDICTED_SERIAL ? this.predictedCast?.yaw : undefined;
    const yaw = castYaw !== undefined
      ? castYaw
      : faceCamera
      ? FACE_CAMERA_YAW
      : reflecting
        ? Math.atan2(hero.pos.x - hero.prevPos.x, hero.pos.z - hero.prevPos.z)
        : this.predictMoving
          ? this.predictYaw
          : hero.facing;
    this.root.rotation.y = lerpAngle(this.root.rotation.y, yaw, 1 - Math.exp(-(reflecting ? 30 : 16) * dt));
    this.applyJump(x, z);
    this.applySlam(x, z, alpha, dt);
    this.play(desired);
    this.advanceClip(dt);

    // fuera de cámara o en la niebla no se anima: el tiempo se acumula y se
    // recupera de golpe al volver a verse (las poses procedurales van sobre el
    // resultado del mixer, así que también se saltan)
    this.animDebt = Math.min(2, this.animDebt + dt);
    if (animate) {
      this.mixer.update(this.animDebt);
      this.animDebt = 0;
      this.applyIdleGrounding(dt);
      this.updateAfterimages(dt, moving);
    }
    this.updateFireball(dt);
    this.updateJumpBits(dt);
    this.updateBreath(dt);
    this.updateShield(dt);
    this.thorns?.update(dt, this.hero);
    this.stunSwirl.update(dt, this.hero);
    if (animate) {
      this.applyBowPose();
      this.applyReflectorPose();
      this.applyBreathPose();
    }
    this.applySpin(alpha);
    this.applyLift(x, z, alpha);
    this.recallFx.update(dt, this.hero);
    this.updateBow();
    this.updateBlaster();

    const invulnerable = hero.invuln > 0 && hero.alive;
    const pulse = 0.5 + 0.5 * Math.sin(performance.now() * 0.008);
    this.ringMaterial.color.setHex(
      invulnerable ? 0xffd166 : this.targeted ? TARGET_HEX : TEAM_HEX[hero.team],
    );
    this.ringMaterial.opacity = hero.alive
      ? (hero.isPlayer || this.targeted ? 0.72 + pulse * 0.18 : 0.35 + pulse * 0.1)
      : 0;
    this.markerMaterial.color.setHex(invulnerable ? 0xffd166 : TEAM_HEX[hero.team]);
    this.markerMaterial.opacity = hero.alive ? 0.85 : 0;

    const burning = this.burn && hero.alive && this.burn.clips.has(this.currentKey);
    if (hero.flashTicks > 0) {
      for (const material of this.materials) {
        material.emissive.setHex(0xbfe6ff);
        material.emissiveIntensity = 1.1;
      }
    } else if (burning && this.burn) {
      // en llamas: brillo naranja que titila
      const flicker = 0.75 + 0.25 * Math.sin(performance.now() * 0.03) + Math.random() * 0.15;
      for (const material of this.materials) {
        material.emissive.copy(this.burn.color);
        material.emissiveIntensity = flicker;
      }
    } else {
      for (const material of this.materials) {
        material.emissiveIntensity = 0;
      }
    }

    this.hpSprite.visible = hero.alive;
    if (
      hero.hp !== this.lastHp ||
      hero.shieldHp !== this.lastShield ||
      hero.alive !== this.lastAlive ||
      hero.level !== this.lastLevel
    ) {
      this.lastLevel = hero.level;
      this.lastHp = hero.hp;
      this.lastShield = hero.shieldHp;
      this.lastAlive = hero.alive;
      this.drawHp();
    }
  }

  private desiredClip(moving: boolean): DesiredClip {
    const hero = this.hero;
    const action = hero.action;
    const serial = hero.actionSerial;

    if (!hero.alive || action.kind === 'dead') {
      this.predictedCast = null;
      return { key: 'death', timeScale: 1, serial };
    }

    const predicted = this.predictedCast;
    if (predicted) {
      const key = `cast_${predicted.slot}`;
      if (serial !== predicted.baseSerial) {
        // llegó la acción del servidor: si es este lanzamiento, el clip sigue donde va
        if (action.kind === 'cast' && action.slot === predicted.slot && this.currentKey === key && this.currentSerial === PREDICTED_SERIAL) {
          this.currentSerial = serial;
        }
        this.predictedCast = null;
      } else if (performance.now() > predicted.until) {
        this.predictedCast = null;
      } else {
        const timeScale = this.loopKeys.has(key) ? 1 : this.actionSpeed(key, predicted.duration);
        return { key, timeScale, serial: PREDICTED_SERIAL, ...this.castTiming(key, predicted.slot, predicted.duration) };
      }
    }

    switch (action.kind) {
      case 'attack': {
        const key = `attack${Math.min(action.chain + 1, 3)}`;
        const attacks = getHeroDef(hero.heroId).attacks;
        const strike = attacks[Math.min(action.chain, attacks.length - 1)];
        // la simulación comprime la ventana de golpe con la duración (velocidad de ataque)
        const impactTicks = strike.activeStart * (action.duration / strike.duration);
        return { key, timeScale: this.actionSpeed(key, action.duration), serial, durationTicks: action.duration, impactTicks };
      }
      case 'cast': {
        // agarre en el aire con la presa: golpe a dos manos que cae al aterrizar
        if (this.grabSlamKey && this.actions.has(this.grabSlamKey)) {
          if (hero.liftTicks > 0 && hero.liftDuration > 0 && hero.grabbedBy === 0) this.grabSlamSerial = serial;
          if (this.grabSlamSerial === serial) {
            const lift = hero.liftDuration > 0 ? hero.liftDuration : 30;
            return { key: this.grabSlamKey, timeScale: 1, serial, durationTicks: lift + 10, impactTicks: lift };
          }
        }
        const key = `cast_${action.slot ?? 'q'}`;
        // los clips en bucle (giro) mantienen su velocidad natural
        const timeScale = this.loopKeys.has(key) ? 1 : this.actionSpeed(key, action.duration);
        return { key, timeScale, serial, ...this.castTiming(key, action.slot ?? 'q', action.duration) };
      }
      case 'hit':
        return { key: 'hit', timeScale: this.actionSpeed('hit', action.duration), serial, durationTicks: action.duration };
      default:
        return moving
          ? { key: 'run', timeScale: 1, serial: 0 }
          : { key: 'idle', timeScale: 1, serial: 0 };
    }
  }

  /**
   * Smear frames: mientras suena un clip configurado y el héroe se desplaza, deja
   * cada AFTERIMAGE_INTERVAL una copia de su pose actual, translúcida y teñida,
   * que se desvanece en el lugar. Parece que hubiera varios por la velocidad.
   */
  private updateAfterimages(dt: number, moving: boolean): void {
    for (let i = this.afterimages.length - 1; i >= 0; i--) {
      const ghost = this.afterimages[i];
      ghost.life -= dt;
      const t = Math.max(0, ghost.life / AFTERIMAGE_LIFE);
      for (const material of ghost.materials) material.opacity = 0.42 * t * t;
      if (ghost.life <= 0) {
        ghost.object.removeFromParent();
        for (const material of ghost.materials) material.dispose();
        this.afterimages.splice(i, 1);
      }
    }

    const spec = this.afterimageSpecs.find((s) => s.clips.has(this.currentKey));
    if (!spec || !this.hero.alive || !moving) {
      this.afterimageTimer = 0;
      return;
    }
    this.afterimageTimer -= dt;
    if (this.afterimageTimer > 0) return;
    this.afterimageTimer = AFTERIMAGE_INTERVAL;
    this.spawnAfterimage(spec.color);
  }

  /**
   * Reflector de Melee: prisma hexagonal de caras blancas translúcidas alrededor
   * del héroe, destellos azules de cuatro puntas que titilan sobre las caras y
   * un estallido de rayos de luz detrás.
   */
  private updateShield(dt: number): void {
    const shield = this.shield;
    if (!shield) return;
    const active = this.hero.alive && shield.clips.has(this.actionKey);
    shield.group.visible = active;
    if (!active) return;
    // activación: el hexágono sale pequeño, se pasa de tamaño y se asienta
    const t = this.hero.action.ticks;
    const pop = t < 3 ? 0.35 + 0.9 * (t / 3) : t < 6 ? 1.25 - 0.25 * ((t - 3) / 3) : 1;
    shield.group.scale.setScalar(pop);
    const now = performance.now() * 0.001;
    const pulse = 0.5 + 0.5 * Math.sin(now * 22);
    shield.faces.opacity = 0.5 + pulse * 0.12;
    shield.rim.opacity = 0.35 + pulse * 0.25;
    shield.rays.material.rotation += dt * 0.6;
    shield.rays.material.opacity = 0.55 + Math.random() * 0.25;
    for (const star of shield.stars) {
      const twinkle = Math.max(0, Math.sin(now * 9 + star.phase));
      star.sprite.material.opacity = 0.25 + twinkle * 0.75;
      star.sprite.scale.setScalar(star.size * (0.6 + twinkle * 0.6));
    }
  }

  /**
   * Salto del Super Jump Punch: arco parabólico durante la acción, polvo al
   * despegar y al aterrizar, y monedas doradas al salir el golpe.
   */
  private applyJump(x: number, z: number): void {
    const spec = this.jumpSpec;
    const hero = this.hero;
    const action = hero.action;
    const active = Boolean(spec) && hero.alive && spec!.clips.has(this.actionKey) && action.duration > 0;
    if (!spec || !active) {
      this.root.position.y = 0;
      if (this.groundRing) this.groundRing.position.y = 0.04;
      if (this.jumpLandPending) {
        this.jumpLandPending = false;
        this.spawnDust(x, z);
      }
      return;
    }
    const t = THREE.MathUtils.clamp(action.ticks / action.duration, 0, 1);
    // sube rápido y cae algo más lento, como un salto de verdad
    const rise = t < 0.4 ? Math.sin((t / 0.4) * Math.PI * 0.5) : Math.cos(((t - 0.4) / 0.6) * Math.PI * 0.5);
    this.root.position.y = spec.height * rise;
    if (this.groundRing) this.groundRing.position.y = 0.04 - this.root.position.y;
    if (hero.actionSerial !== this.jumpSerial) {
      this.jumpSerial = hero.actionSerial;
      this.jumpLandPending = true;
      this.spawnDust(x, z);
      if (spec.coins) this.spawnCoins(x, z, spec.bodyHeight);
    }
    if (t >= 0.97 && this.jumpLandPending) {
      this.jumpLandPending = false;
      this.spawnDust(x, z);
    }
  }

  /**
   * Escupir fuego (Q de Bowser): se inclina hacia delante, estira el cuello,
   * alza la cabeza, abre la mandíbula y la cabeza tiembla con la llamarada.
   * Entra en ~4 ticks y sale en los últimos 6.
   */
  private applyBreathPose(): void {
    const pose = this.breathPose;
    if (!pose) return;
    const hero = this.hero;
    const action = hero.action;
    if (!hero.alive || action.kind !== 'cast' || !pose.clips.has(this.currentKey)) return;
    const weight =
      THREE.MathUtils.clamp((action.ticks + 1) / 4, 0, 1) * THREE.MathUtils.clamp((action.duration - action.ticks) / 6, 0, 1);
    if (weight <= 0) return;
    // eje de cabeceo del héroe: con su −X local, rotar +θ inclina el torso hacia delante
    // (comprobado en el juego: con +X se echaba hacia atrás)
    const pitch = this.scratch.set(-1, 0, 0).applyQuaternion(this.root.getWorldQuaternion(breathQ)).normalize();
    const shake = Math.sin(performance.now() * 0.045) * 0.035;
    rotateBoneWorld(pose.chest, pitch, 0.32 * weight);
    for (const bone of pose.neck) rotateBoneWorld(bone, pitch, (0.24 / Math.max(1, pose.neck.length)) * 2 * weight);
    rotateBoneWorld(pose.head, pitch, (-0.42 + shake) * weight);
    rotateBoneWorld(pose.jaw, pitch, 0.55 * weight);
  }

  /** Giro procedural (`spin`): vueltas sobre sí mismo y, si `crouch`, agachado. */
  private applySpin(alpha: number): void {
    if (this.spinSpecs.length === 0) return;
    const hero = this.hero;
    const action = hero.action;
    const spec = hero.alive && action.kind === 'cast' ? this.spinSpecs.find((s) => s.clips.has(this.currentKey)) : undefined;
    if (!spec || action.duration <= 0) {
      if (this.spinActive) {
        this.spinActive = false;
        this.model.rotation.y = 0;
        if (!this.lifted && !this.reflectorPose) this.model.position.y = this.modelBase.y;
      }
      return;
    }
    this.spinActive = true;
    const span = spec.ticks > 0 ? Math.min(spec.ticks, action.duration) : action.duration;
    const t = THREE.MathUtils.clamp((action.ticks + alpha) / span, 0, 1);
    // arranca y frena suave (easeInOut)
    const eased = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
    this.model.rotation.y = eased * spec.turns * Math.PI * 2;
    if (spec.crouch > 0) {
      const whole = THREE.MathUtils.clamp((action.ticks + alpha) / action.duration, 0, 1);
      this.model.position.y = this.modelBase.y - Math.sin(whole * Math.PI) * spec.crouch * this.bodyHeight;
    }
  }

  /**
   * Golpe contra el suelo (`slam`): despega rápido, queda un instante arriba y
   * cae acelerando para aterrizar en el tick de impacto de la simulación; al
   * tocar el suelo, aplastamiento y onda expansiva del radio del golpe.
   */
  private applySlam(x: number, z: number, alpha: number, dt: number): void {
    const spec = this.slamSpec;
    if (!spec) return;
    if (this.squash > 0) {
      this.squash = Math.max(0, this.squash - dt / 0.22);
      const s = Math.sin(this.squash * Math.PI * 0.5);
      this.model.scale.set(this.modelScale.x * (1 + 0.1 * s), this.modelScale.y * (1 - 0.2 * s), this.modelScale.z * (1 + 0.1 * s));
    } else if (this.model.scale.y !== this.modelScale.y) {
      this.model.scale.copy(this.modelScale);
    }
    const hero = this.hero;
    const action = hero.action;
    const active = hero.alive && action.kind === 'cast' && spec.clips.has(this.actionKey);
    if (!active || !action.slot) {
      if (this.slamSerial !== -1 && !this.jumpSpec) {
        this.root.position.y = 0;
        if (this.groundRing) this.groundRing.position.y = 0.04;
      }
      this.slamSerial = -1;
      return;
    }
    const ability = getHeroDef(hero.heroId).abilities[action.slot];
    const impactTick = ability.kind === 'strike' ? ability.strike.activeStart : ability.kind === 'execute' ? ability.execute.strikeTick : -1;
    if (impactTick < 0) return;
    const radius = ability.kind === 'strike' ? ability.strike.range : 1.8;
    if (hero.actionSerial !== this.slamSerial) {
      this.slamSerial = hero.actionSerial;
      this.slamLanded = false;
    }
    const impact = Math.max(2, impactTick);
    const t = action.ticks + alpha;
    let y = 0;
    if (t < impact) {
      const p = t / impact;
      // subida que frena en lo alto y caída que acelera hasta el suelo
      y = p < 0.55 ? Math.sin((p / 0.55) * Math.PI * 0.5) : 1 - ((p - 0.55) / 0.45) ** 2;
      y *= spec.height;
    } else if (!this.slamLanded) {
      this.slamLanded = true;
      this.squash = 1;
      this.spawnShockwave(x, z, radius);
      this.onSlam?.(x, z);
    }
    this.root.position.y = y;
    if (this.groundRing) this.groundRing.position.y = 0.04 - y;
  }

  /** Onda expansiva del radio del golpe, polvo, marca en el suelo y piedras que saltan. */
  private spawnShockwave(x: number, z: number, radius: number): void {
    const world = this.root.parent;
    if (!world) return;
    const flat = (inner: number, outer: number, color: number, opacity: number) => {
      const mesh = new THREE.Mesh(
        new THREE.RingGeometry(inner, outer, 56),
        new THREE.MeshBasicMaterial({ color, transparent: true, opacity, depthWrite: false, side: THREE.DoubleSide, forceSinglePass: true }),
      );
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.set(x, 0.06, z);
      world.add(mesh);
      return mesh;
    };
    // frente de la onda (llega exactamente al radio del aturdimiento) y un eco más tenue
    const wave = flat(0.86, 1, 0xfff1c9, 0.9);
    this.jumpBits.push({ object: wave, vel: new THREE.Vector3(), life: 0.38, maxLife: 0.38, kind: 'wave', size: radius });
    const echo = flat(0.7, 1, 0xd8b98a, 0.45);
    this.jumpBits.push({ object: echo, vel: new THREE.Vector3(), life: 0.6, maxLife: 0.6, kind: 'wave', size: radius * 0.8 });
    // tierra oscurecida donde golpeó
    const scorch = new THREE.Mesh(
      new THREE.CircleGeometry(radius * 0.55, 40),
      new THREE.MeshBasicMaterial({ color: 0x2a1d10, transparent: true, opacity: 0.45, depthWrite: false, forceSinglePass: true }),
    );
    scorch.rotation.x = -Math.PI / 2;
    scorch.position.set(x, 0.045, z);
    world.add(scorch);
    this.jumpBits.push({ object: scorch, vel: new THREE.Vector3(), life: 1.4, maxLife: 1.4, kind: 'scorch' });
    // nubes de polvo que salen en anillo
    puffGeometry ??= new THREE.IcosahedronGeometry(0.35, 1);
    for (let i = 0; i < 12; i++) {
      const angle = (i / 12) * Math.PI * 2 + Math.random() * 0.3;
      const puff = new THREE.Mesh(
        puffGeometry,
        new THREE.MeshBasicMaterial({ color: 0xcdb48e, transparent: true, opacity: 0.6, depthWrite: false }),
      );
      puff.position.set(x + Math.sin(angle) * 0.6, 0.25, z + Math.cos(angle) * 0.6);
      const speed = radius * (1.4 + Math.random() * 0.6);
      this.jumpBits.push({
        object: puff,
        vel: new THREE.Vector3(Math.sin(angle) * speed, 0.6 + Math.random() * 0.8, Math.cos(angle) * speed),
        life: 0.55,
        maxLife: 0.55,
        kind: 'puff',
      });
      world.add(puff);
    }
    // piedras que saltan y caen
    rockGeometry ??= new THREE.DodecahedronGeometry(0.13, 0);
    for (let i = 0; i < 9; i++) {
      const angle = Math.random() * Math.PI * 2;
      const rock = new THREE.Mesh(rockGeometry, new THREE.MeshBasicMaterial({ color: 0x5b4632, transparent: true, opacity: 1 }));
      rock.position.set(x + Math.sin(angle) * 0.8, 0.15, z + Math.cos(angle) * 0.8);
      rock.scale.setScalar(0.7 + Math.random() * 0.9);
      const speed = 2.5 + Math.random() * 3;
      this.jumpBits.push({
        object: rock,
        vel: new THREE.Vector3(Math.sin(angle) * speed, 5 + Math.random() * 4, Math.cos(angle) * speed),
        life: 0.9,
        maxLife: 0.9,
        kind: 'rock',
      });
      world.add(rock);
    }
  }

  /**
   * Aliento de fuego: mientras dura el cono de daño, la boca escupe llamas que
   * avanzan en abanico, crecen, pasan de amarillo a rojo y se desvanecen al
   * llegar al alcance del golpe.
   */
  private updateBreath(dt: number): void {
    for (let i = this.breathFlames.length - 1; i >= 0; i--) {
      const flame = this.breathFlames[i];
      flame.life -= dt;
      const t = 1 - Math.max(0, flame.life / flame.maxLife); // 0 -> 1
      flame.sprite.position.addScaledVector(flame.vel, dt);
      flame.sprite.position.y += dt * 0.5 * t;
      flame.sprite.scale.setScalar(flame.size * (0.35 + 1.4 * t));
      flame.sprite.material.color.setRGB(1, 0.85 - 0.6 * t, 0.35 - 0.3 * t);
      flame.sprite.material.opacity = 0.95 * (1 - t * t);
      if (flame.life <= 0) {
        flame.sprite.removeFromParent();
        flame.sprite.material.dispose();
        this.breathFlames.splice(i, 1);
      }
    }

    const breath = this.breath;
    const action = this.hero.action;
    const world = this.root.parent;
    const active =
      breath && world && this.hero.alive && breath.clips.has(this.actionKey) && action.ticks >= breath.startTick && action.ticks < action.duration - 1;
    if (!active) {
      this.breathTimer = 0;
      return;
    }
    this.breathTimer -= dt;
    if (this.breathTimer > 0) return;
    this.breathTimer = 0.016;

    flameTexture ??= makeGlowTexture(64);
    this.root.updateMatrixWorld(true);
    const mouth = breath.bone.getWorldPosition(new THREE.Vector3());
    const yaw = this.root.rotation.y;
    const forward = new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw));
    mouth.addScaledVector(forward, breath.forward * breath.height);
    mouth.y -= breath.down * breath.height;
    const life = 0.42;
    for (let k = 0; k < 3; k++) {
      const angle = yaw + (Math.random() - 0.5) * THREE.MathUtils.degToRad(breath.arc);
      const speed = (breath.range / life) * (0.75 + Math.random() * 0.35);
      // baja un poco para que las llamas barran el suelo dentro del cono
      const vel = new THREE.Vector3(Math.sin(angle) * speed, -mouth.y / life * (0.55 + Math.random() * 0.3), Math.cos(angle) * speed);
      const sprite = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: flameTexture,
          color: 0xffd060,
          transparent: true,
          opacity: 0.95,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        }),
      );
      sprite.position.copy(mouth);
      const size = breath.height * (0.16 + Math.random() * 0.08);
      sprite.scale.setScalar(size * 0.35);
      world.add(sprite);
      this.breathFlames.push({ sprite, vel, life: life * (0.8 + Math.random() * 0.4), maxLife: life, size });
    }
  }

  /**
   * Agarre aéreo (R de Bowser, como el de Smash): Bowser y su presa suben en
   * arco y caen de golpe; la presa, agarrada delante, da la vuelta y cae de
   * cabeza. Al aterrizar, polvo (la sacudida la pone el golpe del estrellón).
   */
  /** Huesos de los pies (los más bajos en la pose de enlace) y su altura de reposo. */
  private findFootBones(names?: string[]): void {
    this.root.updateMatrixWorld(true);
    const heightOf = (bone: THREE.Object3D) => this.root.worldToLocal(bone.getWorldPosition(this.footTmp.clone())).y;
    // huesos de los pies declarados en el manifiesto (`feet`, los mismos del mapa de Blender)
    for (const name of names ?? []) {
      const bone = this.model.getObjectByName(name);
      if (bone && (bone as THREE.Bone).isBone) this.footBones.push(bone);
    }
    if (this.footBones.length > 0) {
      this.footBaseline = Math.min(...this.footBones.map(heightOf));
      return;
    }
    // sin `feet`: las puntas de cadena más bajas, ignorando la raíz del rig y sus
    // huesos sueltos (efectos, sombra), que están en el suelo y no se mueven
    const bones: Array<{ bone: THREE.Object3D; y: number }> = [];
    this.model.traverse((object) => {
      if (!(object as THREE.Bone).isBone) return;
      if (object.children.some((child) => (child as THREE.Bone).isBone)) return;
      let depth = 0;
      for (let parent = object.parent; parent && (parent as THREE.Bone).isBone; parent = parent.parent) depth++;
      if (depth < 4) return;
      bones.push({ bone: object, y: heightOf(object) });
    });
    if (bones.length === 0) return;
    bones.sort((x, y) => x.y - y.y);
    const lowest = bones[0].y;
    for (const entry of bones) {
      if (entry.y > lowest + this.bodyHeight * 0.12 || this.footBones.length >= 6) break;
      this.footBones.push(entry.bone);
    }
    this.footBaseline = lowest;
  }

  /** Altura del pie más bajo respecto al héroe, con el modelo en su sitio. */
  private lowestFootY(): number {
    let lowest = Infinity;
    for (const bone of this.footBones) {
      const y = this.root.worldToLocal(bone.getWorldPosition(this.footTmp)).y;
      if (y < lowest) lowest = y;
    }
    return lowest;
  }

  private applyIdleGrounding(dt: number): void {
    if (this.footBones.length === 0 || this.lifted || this.reflectorPose) return;
    const idle = this.hero.alive && this.currentKey === 'idle';
    if (idle) {
      this.model.position.y = this.modelBase.y;
      this.root.updateMatrixWorld(true);
      // sólo se corrige hacia abajo: nunca hunde los pies bajo su altura de reposo
      this.groundOffset = Math.max(0, this.lowestFootY() - this.footBaseline);
    } else if (this.groundOffset > 0) {
      // al salir del idle vuelve a su sitio en unas décimas, sin saltos
      this.groundOffset = Math.max(0, this.groundOffset - dt * 1.5);
    } else {
      return;
    }
    this.model.position.y = this.modelBase.y - this.groundOffset;
  }

  private applyLift(x: number, z: number, alpha: number): void {
    const hero = this.hero;
    const lifting = hero.alive && hero.liftTicks > 0 && hero.liftDuration > 0;
    if (!lifting) {
      if (this.lifted) {
        this.lifted = false;
        this.model.rotation.x = 0;
        this.model.position.z = this.modelBase.z;
        if (!this.reflectorPose) this.model.position.y = this.modelBase.y;
        this.spawnDust(x, z);
      }
      return;
    }
    this.lifted = true;
    const t = THREE.MathUtils.clamp((hero.liftTicks - 1 + alpha) / hero.liftDuration, 0, 1);
    // sube rápido, queda arriba un instante y cae acelerando
    const rise = t < 0.55 ? Math.sin((t / 0.55) * Math.PI * 0.5) : 1 - ((t - 0.55) / 0.45) ** 2;
    const held = hero.grabbedBy !== 0;
    // la presa va levantada por encima de Bowser (si no, su cuerpo la tapa) y cae de cabeza
    const height = LIFT_HEIGHT * rise + (held ? HELD_EXTRA_HEIGHT * rise : 0);
    this.root.position.y = height;
    if (this.groundRing) this.groundRing.position.y = 0.04 - height;
    if (!held) return;
    // voltereta alrededor del centro del cuerpo: acaba cabeza abajo
    const flip = Math.PI * THREE.MathUtils.smoothstep(t, 0.35, 0.8);
    const half = this.bodyHeight * 0.5;
    this.model.rotation.x = flip;
    this.model.position.y = this.modelBase.y + half * (1 - Math.cos(flip));
    this.model.position.z = this.modelBase.z - half * Math.sin(flip);
  }

  private spawnDust(x: number, z: number): void {
    const world = this.root.parent;
    if (!world) return;
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(0.35, 0.6, 32),
      new THREE.MeshBasicMaterial({ color: 0xe8dcc4, transparent: true, opacity: 0.7, depthWrite: false, side: THREE.DoubleSide, forceSinglePass: true }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.set(x, 0.05, z);
    world.add(ring);
    this.jumpBits.push({ object: ring, vel: new THREE.Vector3(), life: 0.45, maxLife: 0.45, kind: 'dust' });
  }

  private spawnCoins(x: number, z: number, height: number): void {
    const world = this.root.parent;
    if (!world) return;
    coinGeometry ??= new THREE.CylinderGeometry(0.13, 0.13, 0.035, 18);
    for (let i = 0; i < 5; i++) {
      const coin = new THREE.Mesh(
        coinGeometry,
        new THREE.MeshStandardMaterial({ color: 0xffd23f, emissive: 0x8a5a00, metalness: 0.6, roughness: 0.3, transparent: true }),
      );
      coin.rotation.z = Math.random() * Math.PI;
      coin.position.set(x, height * 0.9, z);
      world.add(coin);
      const angle = (i / 5) * Math.PI * 2 + Math.random() * 0.6;
      const vel = new THREE.Vector3(Math.cos(angle) * 2.2, 6.5 + Math.random() * 2, Math.sin(angle) * 2.2);
      this.jumpBits.push({ object: coin, vel, life: 0.9, maxLife: 0.9, kind: 'coin' });
    }
  }

  private updateJumpBits(dt: number): void {
    for (let i = this.jumpBits.length - 1; i >= 0; i--) {
      const bit = this.jumpBits[i];
      bit.life -= dt;
      const t = Math.max(0, bit.life / bit.maxLife);
      const material = (bit.object as THREE.Mesh).material as THREE.Material & { opacity: number };
      if (bit.kind === 'dust') {
        bit.object.scale.setScalar(1 + (1 - t) * 2.4);
        material.opacity = 0.7 * t;
      } else if (bit.kind === 'wave') {
        // crece rápido al principio y frena al llegar al radio
        const grow = 1 - (1 - (1 - t)) ** 3;
        bit.object.scale.setScalar(0.3 + (bit.size ?? 1) * grow);
        material.opacity = 0.9 * t;
      } else if (bit.kind === 'scorch') {
        material.opacity = 0.45 * Math.min(1, t * 1.6);
      } else if (bit.kind === 'puff') {
        bit.vel.multiplyScalar(Math.exp(-5 * dt));
        bit.object.position.addScaledVector(bit.vel, dt);
        bit.object.scale.setScalar(1 + (1 - t) * 2.2);
        material.opacity = 0.6 * t;
      } else if (bit.kind === 'rock') {
        bit.vel.y -= 22 * dt;
        bit.object.position.addScaledVector(bit.vel, dt);
        if (bit.object.position.y < 0.08) {
          bit.object.position.y = 0.08;
          bit.vel.set(bit.vel.x * 0.4, Math.abs(bit.vel.y) * 0.25, bit.vel.z * 0.4);
        }
        bit.object.rotation.x += dt * 8;
        bit.object.rotation.z += dt * 6;
        material.opacity = Math.min(1, t * 3);
      } else {
        bit.vel.y -= 18 * dt;
        bit.object.position.addScaledVector(bit.vel, dt);
        // voltean de canto a cara, como las monedas de Melee
        bit.object.rotation.x += dt * 9;
        material.opacity = Math.min(1, t * 2.5);
      }
      if (bit.life <= 0) {
        bit.object.removeFromParent();
        material.dispose();
        if (bit.kind === 'dust' || bit.kind === 'wave' || bit.kind === 'scorch') (bit.object as THREE.Mesh).geometry.dispose();
        this.jumpBits.splice(i, 1);
      }
    }
  }

  /** Bola de fuego superpuesta al héroe + estela de llamas en el mundo. */
  private updateFireball(dt: number): void {
    for (let i = this.flames.length - 1; i >= 0; i--) {
      const flame = this.flames[i];
      flame.life -= dt;
      const t = Math.max(0, flame.life / FLAME_LIFE);
      flame.sprite.material.opacity = 0.85 * t;
      flame.sprite.scale.setScalar(flame.size * (0.45 + 0.55 * t));
      flame.sprite.position.y += dt * 0.9;
      if (flame.life <= 0) {
        flame.sprite.removeFromParent();
        flame.sprite.material.dispose();
        this.flames.splice(i, 1);
      }
    }

    const ball = this.fireball;
    if (!ball) return;
    const active = this.hero.alive && ball.clips.has(this.currentKey);
    ball.group.visible = active;
    if (!active) {
      this.flameTimer = 0;
      return;
    }
    // titileo: el cometa late y la capa exterior gira
    const now = performance.now();
    const flicker = 1 + Math.sin(now * 0.04) * 0.06 + (Math.random() - 0.5) * 0.08;
    ball.core.scale.setScalar(flicker);
    ball.shell.scale.set(flicker * 1.05, flicker, flicker * 1.55);
    ball.shell.rotation.z += dt * 9;
    (ball.glow.material as THREE.SpriteMaterial).opacity = 0.75 + Math.random() * 0.2;
    ball.glow.scale.setScalar(ball.radius * (4.2 + Math.random() * 0.6));

    const world = this.root.parent;
    if (!world) return;
    this.flameTimer -= dt;
    if (this.flameTimer > 0) return;
    this.flameTimer = FLAME_INTERVAL;
    const sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: flameTexture,
        color: Math.random() < 0.5 ? 0xff7a1a : 0xffc04a,
        transparent: true,
        opacity: 0.85,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    ball.group.getWorldPosition(sprite.position);
    sprite.position.x += (Math.random() - 0.5) * ball.radius * 0.8;
    sprite.position.z += (Math.random() - 0.5) * ball.radius * 0.8;
    const size = ball.radius * (1.6 + Math.random() * 0.8);
    sprite.scale.setScalar(size);
    world.add(sprite);
    this.flames.push({ sprite, life: FLAME_LIFE, size });
  }

  private spawnAfterimage(color: THREE.Color): void {
    const world = this.root.parent;
    if (!world) return;
    this.model.updateMatrixWorld(true);
    // el clon del esqueleto conserva la pose de este instante y queda congelado
    const ghost = cloneSkeleton(this.model);
    const materials: THREE.MeshBasicMaterial[] = [];
    ghost.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh) return;
      const material = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.42,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      mesh.material = material;
      mesh.castShadow = false;
      mesh.frustumCulled = false;
      materials.push(material);
    });
    this.model.matrixWorld.decompose(ghost.position, ghost.quaternion, ghost.scale);
    world.add(ghost);
    this.afterimages.push({ object: ghost, materials, life: AFTERIMAGE_LIFE });
  }

  /**
   * Pose de arquero sobre la animación: brazo del arco extendido al frente y el
   * otro tensando la cuerda junto a la cara. Las direcciones están en el espacio
   * del héroe (+Z adelante, +X su izquierda); cada hueso se gira para apuntar a
   * su hijo en esa dirección, así no depende de la orientación de los huesos.
   */
  private applyBowPose(): void {
    const pose = this.bowPose;
    const bow = this.bow;
    if (!pose || !bow || this.currentKey !== bow.clip) return;
    const action = this.hero.action;
    const ramp = 4;
    const weight =
      THREE.MathUtils.clamp(action.ticks / ramp, 0, 1) *
      THREE.MathUtils.clamp((action.duration - action.ticks) / ramp, 0, 1);
    if (weight <= 0) return;

    const rootQ = this.root.getWorldQuaternion(new THREE.Quaternion());
    const dir = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z).normalize().applyQuaternion(rootQ);
    // ¿de qué lado está el brazo del arco? (+X = izquierda del héroe)
    pose.bowArm[0].getWorldPosition(this.scratch);
    this.root.worldToLocal(this.scratch);
    const s = this.scratch.x >= 0 ? 1 : -1;
    aimBone(pose.bowArm[0], pose.bowArm[1], dir(0.25 * s, 0.08, 1), weight);
    aimBone(pose.bowArm[1], pose.bowArm[2], dir(0.1 * s, 0.05, 1), weight);
    aimBone(pose.drawArm[0], pose.drawArm[1], dir(-0.7 * s, 0.1, -0.5), weight);
    aimBone(pose.drawArm[1], pose.drawArm[2], dir(0.75 * s, 0.15, 0.6), weight);
  }

  /**
   * Pose del Reflector (Shine) de Melee: entra casi al instante (el escudo sale
   * en el frame 1) y al terminar se levanta en ~1/6 s.
   * Caderas abajo, piernas abiertas con las rodillas hacia fuera y los pies
   * plantados (IK de dos huesos), torso erguido, brazos abiertos hacia abajo
   * con las manos a la altura de la cadera.
   */
  private applyReflectorPose(): void {
    const pose = this.reflectorPose;
    if (!pose) return;
    const action = this.hero.action;
    const active = this.hero.alive && pose.clips.has(this.currentKey);
    const weight = active
      ? THREE.MathUtils.clamp((action.ticks + 1) / 2, 0, 1) *
        THREE.MathUtils.clamp((action.duration - action.ticks) / 5, 0, 1)
      : 0;
    if (weight <= 0 || pose.baked) {
      this.model.position.y = pose.baseY;
      return;
    }
    const height = pose.height;
    this.root.updateMatrixWorld(true);
    const local = (bone: THREE.Object3D) => this.root.worldToLocal(bone.getWorldPosition(new THREE.Vector3()));

    // altura de los pies con la animación base: ahí se quedan plantados
    const footY = Math.min(...pose.legs.map((leg) => local(leg[2]).y));
    this.model.position.y = pose.baseY - pose.squat * height * weight;
    this.model.updateMatrixWorld(true);

    const rootQ = this.root.getWorldQuaternion(new THREE.Quaternion());
    const toWorldDir = (v: THREE.Vector3) => v.clone().normalize().applyQuaternion(rootQ);
    const dir = (x: number, y: number, z: number) => toWorldDir(new THREE.Vector3(x, y, z));

    // torso erguido con la cabeza un poco hacia delante
    aimBone(pose.spine[0], pose.spine[1], dir(0, 1, 0.12), weight);
    aimBone(pose.spine[1], pose.spine[2], dir(0, 1, 0.22), weight);

    for (const [upper, lower, foot] of pose.legs) {
      const hip = local(upper);
      const s = hip.x >= 0 ? 1 : -1;
      const l1 = local(lower).distanceTo(hip);
      const l2 = local(foot).distanceTo(local(lower));
      const target = new THREE.Vector3(s * height * 0.25, footY, height * 0.03);
      const toFoot = target.clone().sub(hip);
      const d = THREE.MathUtils.clamp(toFoot.length(), 1e-4, (l1 + l2) * 0.999);
      const u = toFoot.normalize();
      // la rodilla apunta hacia fuera y adelante
      const pole = new THREE.Vector3(s * 0.75, 0, 0.65);
      pole.sub(u.clone().multiplyScalar(pole.dot(u))).normalize();
      const a = (l1 * l1 - l2 * l2 + d * d) / (2 * d);
      const h = Math.sqrt(Math.max(0, l1 * l1 - a * a));
      const knee = hip.clone().addScaledVector(u, a).addScaledVector(pole, h);
      const footAt = hip.clone().addScaledVector(u, d);
      aimBone(upper, lower, toWorldDir(knee.clone().sub(hip)), weight);
      aimBone(lower, foot, toWorldDir(footAt.sub(knee)), weight);
    }

    for (const [upper, lower, hand] of pose.arms) {
      const s = local(upper).x >= 0 ? 1 : -1;
      // brazo abierto hacia abajo; antebrazo de vuelta hacia la cadera
      aimBone(upper, lower, dir(s * 0.8, -0.55, 0.1), weight);
      aimBone(lower, hand, dir(-s * 0.3, -0.25, 0.65), weight);
    }
  }

  /** El arco sigue a la mano más adelantada (en el espacio del héroe) mientras se dispara. */
  private updateBow(): void {
    const bow = this.bow;
    if (!bow) return;
    const show = this.hero.alive && this.currentKey === bow.clip;
    bow.group.visible = show;
    if (!show) return;
    this.root.updateMatrixWorld(true);
    let best = -Infinity;
    for (const bone of bow.bones) {
      bone.getWorldPosition(this.scratch);
      this.root.worldToLocal(this.scratch);
      if (this.scratch.z > best) {
        best = this.scratch.z;
        bow.group.position.copy(this.scratch);
      }
    }
  }

  /** La pistola sigue a la mano más adelantada (en el espacio del héroe) mientras dispara. */
  private updateBlaster(): void {
    const blaster = this.blaster;
    if (!blaster) return;
    const show = this.hero.alive && blaster.clips.has(this.currentKey);
    blaster.group.visible = show;
    if (!show) return;
    this.root.updateMatrixWorld(true);
    let best = -Infinity;
    for (const bone of blaster.bones) {
      bone.getWorldPosition(this.scratch);
      this.root.worldToLocal(this.scratch);
      if (this.scratch.z > best) {
        best = this.scratch.z;
        blaster.group.position.copy(this.scratch);
      }
    }
  }

  /** Duración e impacto de una habilidad para `ClipWarp` (los bucles van a su ritmo). */
  private castTiming(key: string, slot: AbilitySlot, durationTicks: number): Pick<DesiredClip, 'durationTicks' | 'impactTicks'> {
    if (this.loopKeys.has(key)) return {};
    const ability = getHeroDef(this.hero.heroId).abilities[slot];
    let impactTicks: number | undefined;
    if (ability.kind === 'strike') impactTicks = ability.strike.activeStart;
    else if (ability.kind === 'projectile' && ability.projectile.fireAtTick > 0) impactTicks = ability.projectile.fireAtTick;
    else if (ability.kind === 'execute') impactTicks = ability.execute.strikeTick;
    // agarre: la embestida dura lo que el dash (el resto lo cubre el estrellón)
    else if (ability.kind === 'grab') return { durationTicks: ability.grab.dashTicks + 4 };
    return { durationTicks, impactTicks };
  }

  /** Tiempo del clip para lo que lleva la acción (ver `ClipWarp`). */
  private warpTime(warp: ClipWarp): number {
    const { start, end, impact, impactT, durationT, elapsed } = warp;
    if (impact !== undefined && impactT > 0 && impactT < durationT) {
      if (elapsed <= impactT) return start + (impact - start) * (elapsed / impactT);
      return impact + (end - impact) * Math.min(1, (elapsed - impactT) / (durationT - impactT));
    }
    return start + (end - start) * Math.min(1, elapsed / Math.max(1e-3, durationT));
  }

  /** Avanza el golpe en curso (tiempo fijado a mano) o ajusta el ritmo de la carrera. */
  private advanceClip(dt: number): void {
    const action = this.currentAction;
    if (!action) return;
    if (this.warp) {
      this.warp.elapsed += dt;
      action.time = this.warpTime(this.warp);
      return;
    }
    if (this.currentKey === 'run' && this.runSpeed > 0) {
      const hero = this.hero;
      const statsKey = `${hero.level}|${hero.items.join(',')}`;
      if (statsKey !== this.runStatsKey) {
        this.runStatsKey = statsKey;
        this.runMoveSpeed = heroStats(hero.heroId, hero.level, hero.items).moveSpeed;
      }
      // los pies acompañan al suelo: sin patinar ni hacer moonwalk
      action.timeScale = THREE.MathUtils.clamp(this.runMoveSpeed / this.runSpeed, 0.6, 2);
    }
  }

  private actionSpeed(key: string, durationTicks: number): number {
    const duration = this.clipDurations.get(key);
    if (!duration || durationTicks <= 0) return 1;
    return THREE.MathUtils.clamp(duration / (durationTicks * TICK_DT), 0.55, 3.2);
  }

  private play(desired: DesiredClip): void {
    const action = this.actions.get(desired.key) ?? this.actions.get('idle');
    if (!action) return;

    if (this.currentKey === desired.key && this.currentSerial === desired.serial) {
      if (!this.warp && desired.key !== 'run') action.timeScale = desired.timeScale;
      return;
    }

    const loop = this.loopKeys.has(desired.key);
    const previous = this.currentAction;

    action.reset();
    action.setLoop(loop ? THREE.LoopRepeat : THREE.LoopOnce, loop ? Infinity : 1);
    action.clampWhenFinished = !loop;
    action.timeScale = desired.timeScale;
    action.enabled = true;
    action.setEffectiveWeight(1);
    action.play();

    if (previous && previous !== action) {
      previous.crossFadeTo(action, fadeTime(this.currentKey, desired.key), false);
    }

    // golpes y habilidades: el tiempo del clip sigue a la acción de la simulación
    this.warp = null;
    const clipDuration = this.clipDurations.get(desired.key) ?? 0;
    if (!loop && desired.durationTicks && desired.durationTicks > 0 && clipDuration > 0) {
      const timing = this.timings[desired.key] ?? {};
      const start = THREE.MathUtils.clamp(timing.start ?? 0, 0, clipDuration);
      const end = THREE.MathUtils.clamp(timing.end ?? clipDuration, start + 0.01, clipDuration);
      const impact = timing.impact !== undefined && timing.impact > start && timing.impact < end ? timing.impact : undefined;
      this.warp = {
        start,
        end,
        impact,
        impactT: (desired.impactTicks ?? 0) * TICK_DT,
        durationT: desired.durationTicks * TICK_DT,
        elapsed: 0,
      };
      action.timeScale = 0;
      action.time = start;
    }

    this.currentAction = action;
    this.currentKey = desired.key;
    this.currentSerial = desired.serial;
  }

  private drawHp(): void {
    const hero = this.hero;
    const ctx = this.hpCanvas.getContext('2d');
    if (!ctx) return;

    const width = this.hpCanvas.width;
    const height = this.hpCanvas.height;
    ctx.clearRect(0, 0, width, height);

    ctx.textBaseline = 'middle';
    ctx.font = '700 15px Bahnschrift, Segoe UI, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillStyle = 'rgba(228,242,255,0.94)';
    ctx.fillText(`${hero.level}  ${heroDisplayName(hero.name)}`, 6, 15);

    ctx.font = '600 13px Bahnschrift, Segoe UI, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillStyle = 'rgba(186,212,234,0.82)';
    ctx.fillText(`${Math.ceil(hero.hp)}`, width - 6, 15);

    roundedRect(ctx, 4, 27, width - 8, 21, 10);
    ctx.fillStyle = 'rgba(4,8,12,0.8)';
    ctx.fill();

    // con escudo, la barra se reparte entre vida y escudo (tramo blanco, como en LoL)
    const total = Math.max(hero.maxHp, hero.hp + hero.shieldHp);
    const ratio = THREE.MathUtils.clamp(hero.hp / total, 0, 1);
    if (hero.shieldHp > 0) {
      const start = 7 + (width - 14) * ratio;
      roundedRect(ctx, Math.max(7, start - 7), 30, Math.max(2, (width - 14) * (hero.shieldHp / total) + 7), 15, 7);
      ctx.fillStyle = 'rgba(236, 241, 248, 0.95)';
      ctx.fill();
    }
    if (ratio > 0) {
      const fillWidth = (width - 14) * ratio;
      const gradient = ctx.createLinearGradient(6, 0, width - 6, 0);
      if (ratio > 0.5) {
        gradient.addColorStop(0, '#3fb8ff');
        gradient.addColorStop(1, '#7ce6c0');
      } else if (ratio > 0.25) {
        gradient.addColorStop(0, '#ffb84d');
        gradient.addColorStop(1, '#ffe08a');
      } else {
        gradient.addColorStop(0, '#ff5a5a');
        gradient.addColorStop(1, '#ff9a6b');
      }
      roundedRect(ctx, 7, 30, Math.max(2, fillWidth), 15, 7);
      ctx.fillStyle = gradient;
      ctx.fill();
    }

    roundedRect(ctx, 4, 27, width - 8, 21, 10);
    ctx.strokeStyle = TEAM_CSS[hero.team];
    ctx.globalAlpha = 0.85;
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.globalAlpha = 1;

    this.hpTexture.needsUpdate = true;
  }
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

/**
 * Arco simple: madera curvada hacia delante (+Z del héroe) y cuerda recta.
 * Melee no trae el arco de Link en el modelo del luchador, así que se genera aquí.
 */
function makeBow(heroHeight: number): THREE.Group {
  const group = new THREE.Group();
  const half = heroHeight * 0.3;
  const points: THREE.Vector3[] = [];
  for (let i = 0; i <= 16; i++) {
    const t = (i / 16) * 2 - 1;
    points.push(new THREE.Vector3(0, t * half, (1 - t * t) * half * 0.38));
  }
  const wood = new THREE.Mesh(
    new THREE.TubeGeometry(new THREE.CatmullRomCurve3(points), 32, heroHeight * 0.014, 6),
    new THREE.MeshStandardMaterial({ color: 0x7a4a22, roughness: 0.6 }),
  );
  wood.castShadow = true;
  group.add(wood);
  const string = new THREE.Mesh(
    new THREE.CylinderGeometry(heroHeight * 0.003, heroHeight * 0.003, half * 2, 4),
    new THREE.MeshBasicMaterial({ color: 0xeee6cf }),
  );
  group.add(string);
  return group;
}

/**
 * Pistola bláster simple (cañón, cuerpo, empuñadura y brillo cian) apuntando
 * hacia delante (+Z del héroe). Melee no trae el bláster de Falco en el modelo.
 */
function makeBlaster(heroHeight: number): THREE.Group {
  const group = new THREE.Group();
  const h = heroHeight;
  // el Blaster de verdad (WpnFoxBlaster de Melee, assets/props/blaster.glb):
  // el cañón apunta a +Z; se desplaza para que la empuñadura quede en la mano
  const real = propModels.get('blaster');
  if (real) {
    const gun = real.clone(true);
    const s = h / 1.9;
    gun.scale.setScalar(s);
    gun.position.set(0, -0.12 * s, 0.15 * s);
    group.add(gun);
    return group;
  }
  const metal = new THREE.MeshStandardMaterial({ color: 0x3a4250, roughness: 0.45, metalness: 0.7 });
  const dark = new THREE.MeshStandardMaterial({ color: 0x14181f, roughness: 0.6, metalness: 0.4 });
  const glow = new THREE.MeshStandardMaterial({
    color: 0x6fe8ff,
    emissive: 0x2fb6ff,
    emissiveIntensity: 1.5,
    roughness: 0.3,
  });

  const barrel = new THREE.Mesh(new THREE.BoxGeometry(h * 0.055, h * 0.05, h * 0.24), metal);
  barrel.position.set(0, h * 0.035, h * 0.1);
  group.add(barrel);

  const body = new THREE.Mesh(new THREE.BoxGeometry(h * 0.075, h * 0.075, h * 0.16), dark);
  body.position.set(0, h * 0.02, h * 0.02);
  group.add(body);

  const grip = new THREE.Mesh(new THREE.BoxGeometry(h * 0.06, h * 0.15, h * 0.07), dark);
  grip.position.set(0, -h * 0.06, -h * 0.03);
  grip.rotation.x = 0.28;
  group.add(grip);

  const muzzle = new THREE.Mesh(new THREE.CylinderGeometry(h * 0.03, h * 0.03, h * 0.03, 12), glow);
  muzzle.rotation.x = Math.PI / 2;
  muzzle.position.set(0, h * 0.035, h * 0.225);
  group.add(muzzle);

  const strip = new THREE.Mesh(new THREE.BoxGeometry(h * 0.08, h * 0.012, h * 0.1), glow);
  strip.position.set(0, h * 0.06, h * 0.04);
  group.add(strip);

  group.traverse((object) => {
    const mesh = object as THREE.Mesh;
    if (mesh.isMesh) mesh.castShadow = true;
  });
  return group;
}

const aimFrom = new THREE.Vector3();
const aimTo = new THREE.Vector3();
const aimQ = new THREE.Quaternion();
const boneQ = new THREE.Quaternion();
const parentQ = new THREE.Quaternion();

/** Gira `bone` (mezclando con `weight`) para que su hijo quede en la dirección `worldDir`. */
interface BreathFlame {
  sprite: THREE.Sprite;
  vel: THREE.Vector3;
  life: number;
  maxLife: number;
  size: number;
}

interface JumpBit {
  object: THREE.Object3D;
  vel: THREE.Vector3;
  life: number;
  maxLife: number;
  kind: 'dust' | 'coin' | 'wave' | 'scorch' | 'puff' | 'rock';
  /** radio final (ondas) */
  size?: number;
}

let puffGeometry: THREE.IcosahedronGeometry | undefined;
let rockGeometry: THREE.DodecahedronGeometry | undefined;

let coinGeometry: THREE.CylinderGeometry | undefined;

const breathQ = new THREE.Quaternion();
const turnQ = new THREE.Quaternion();

/** Gira un hueso `angle` rad alrededor de un eje del mundo (sobre la pose del clip). */
function rotateBoneWorld(bone: THREE.Object3D, worldAxis: THREE.Vector3, angle: number): void {
  if (Math.abs(angle) < 1e-5) return;
  turnQ.setFromAxisAngle(worldAxis, angle);
  bone.getWorldQuaternion(boneQ);
  boneQ.premultiply(turnQ);
  if (bone.parent) {
    bone.parent.getWorldQuaternion(parentQ);
    boneQ.premultiply(parentQ.invert());
  }
  bone.quaternion.copy(boneQ);
  bone.updateMatrixWorld(true);
}

function aimBone(bone: THREE.Object3D, child: THREE.Object3D, worldDir: THREE.Vector3, weight: number): void {
  bone.getWorldPosition(aimFrom);
  child.getWorldPosition(aimTo);
  aimTo.sub(aimFrom);
  if (aimTo.lengthSq() < 1e-10) return;
  aimQ.setFromUnitVectors(aimTo.normalize(), worldDir);
  bone.getWorldQuaternion(boneQ);
  boneQ.premultiply(aimQ);
  if (bone.parent) {
    bone.parent.getWorldQuaternion(parentQ);
    boneQ.premultiply(parentQ.invert());
  }
  bone.quaternion.slerp(boneQ, weight);
  bone.updateMatrixWorld(true);
}

/**
 * Bola de fuego del tamaño del héroe: núcleo brillante, capa exterior alargada
 * hacia atrás (cometa que apunta a +Z, hacia donde mira) y resplandor.
 */
function makeFireball(heroHeight: number, clips: Set<string>): Fireball {
  flameTexture ??= makeGlowTexture(64);
  const radius = heroHeight * 0.42;
  const group = new THREE.Group();
  group.position.y = heroHeight * 0.5;
  group.visible = false;

  const core = new THREE.Mesh(
    new THREE.SphereGeometry(radius * 0.78, 20, 14),
    new THREE.MeshBasicMaterial({
      color: 0xffd27a,
      transparent: true,
      opacity: 0.55,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  group.add(core);

  // cometa: esfera estirada en Z y corrida hacia atrás
  const shellGeometry = new THREE.SphereGeometry(radius, 20, 14);
  shellGeometry.translate(0, 0, -radius * 0.25);
  const shell = new THREE.Mesh(
    shellGeometry,
    new THREE.MeshBasicMaterial({
      color: 0xff5a10,
      transparent: true,
      opacity: 0.42,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide, forceSinglePass: true,
    }),
  );
  group.add(shell);

  const glow = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: flameTexture,
      color: 0xff8a2a,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  glow.scale.setScalar(radius * 4.4);
  group.add(glow);

  return { clips, group, core, shell, glow, radius, height: heroHeight };
}

/**
 * Reflector ("Shine") como en Melee: prisma hexagonal vertical que envuelve al
 * héroe, con caras casi blancas y translúcidas (borde levemente azulado), destellos
 * azules de cuatro puntas sobre las caras y un estallido de rayos de luz.
 */
function makeShield(heroHeight: number, clips: Set<string>, color: THREE.Color): Shield {
  starTexture ??= makeStarTexture();
  raysTexture ??= makeRaysTexture();
  const height = heroHeight * 1.15;
  const radius = height * 0.5;
  const group = new THREE.Group();
  group.position.y = heroHeight * 0.52;
  group.visible = false;

  // hexágono plano que siempre mira a la cámara (sprite), del alto del héroe
  hexTexture ??= makeHexTexture();
  hexRimTexture ??= makeHexRimTexture();
  const rim = new THREE.SpriteMaterial({
    map: hexRimTexture,
    color,
    transparent: true,
    opacity: 0.5,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const rimSprite = new THREE.Sprite(rim);
  rimSprite.scale.setScalar(height * 1.18);
  group.add(rimSprite);
  const faces = new THREE.SpriteMaterial({
    map: hexTexture,
    transparent: true,
    opacity: 0.6,
    depthWrite: false,
  });
  const hex = new THREE.Sprite(faces);
  hex.scale.setScalar(height);
  hex.renderOrder = 2;
  group.add(hex);

  const rays = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: raysTexture,
      color: 0xdff4ff,
      transparent: true,
      opacity: 0.6,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  rays.scale.setScalar(heroHeight * 2.6);
  rays.renderOrder = -1;
  group.add(rays);

  // destellos azules de cuatro puntas repartidos sobre las caras
  const stars: Shield['stars'] = [];
  for (let i = 0; i < 9; i++) {
    const sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: starTexture,
        // azules sobre el blanco de las caras (mezcla normal para que no se laven a blanco)
        color: i % 3 === 0 ? 0x7cc4ff : 0x1f6fff,
        transparent: true,
        opacity: 0.8,
        depthWrite: false,
      }),
    );
    // repartidos dentro del hexágono (delante del panel)
    const a = i * 2.39996;
    const d = Math.sqrt((i + 0.5) / 9) * height * 0.36;
    sprite.position.set(Math.cos(a) * d * 0.7, Math.sin(a) * d, -Math.cos(a) * d * 0.7);
    sprite.renderOrder = 3;
    const size = heroHeight * (0.18 + ((i * 7) % 4) * 0.04);
    sprite.scale.setScalar(size);
    group.add(sprite);
    stars.push({ sprite, phase: i * 1.7, size });
  }

  return { clips, group, faces, rim, rays, stars, height, radius };
}
